(() => {
  if (customElements.get('nomosu-product')) return;
  class ScopedElement extends HTMLElement {
    connectedCallback() { this.abort?.abort(); this.abort = new AbortController(); this.start(); }
    disconnectedCallback() { this.abort?.abort(); }
    listen(target, event, handler) { target.addEventListener(event, handler, { signal: this.abort.signal }); }
    start() {}
  }
  class Product extends ScopedElement {
    start() {
      this.listen(this, 'click', event => {
        const button = event.target.closest('[data-quantity-change]');
        if (!button) return;
        const input = this.querySelector('[name="quantity"]');
        if (!input) return;
        if (button.dataset.quantityChange === '1') input.stepUp(); else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.listen(this, 'change', event => {
        if (!event.target.matches('[data-variant-select]')) return;
        const option = event.target.selectedOptions[0];
        const price = this.querySelector('[data-product-price]');
        if (price) price.textContent = option.dataset.price;
      });
    }
  }
  class Accordions extends ScopedElement {
    start() {
      this.listen(this, 'click', event => {
        const button = event.target.closest('[data-accordion-toggle]');
        if (!button || !this.contains(button)) return;
        const body = button.nextElementSibling;
        if (!body) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        body.hidden = !open; body.classList.toggle('open', open);
        const symbol = button.querySelector('.accordion-symbol');
        if (symbol) symbol.textContent = open ? '−' : '+';
        button.querySelector('.chevron')?.classList.toggle('open', open);
      });
      this.listen(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const button = event.target.querySelector('[data-accordion-toggle]');
        if (button && button.getAttribute('aria-expanded') !== 'true') button.click();
      });
    }
  }
  class RetailForm extends ScopedElement {
    start() {
      const form = this.querySelector('form');
      if (!form) return;
      const inputs = [...form.querySelectorAll('[data-contact-field]')];
      const validate = input => {
        const error = this.querySelector(`[id="${input.getAttribute('aria-describedby')}"]`);
        let message = '';
        if (input.required && !input.value.trim()) message = this.dataset.requiredMessage;
        else if (input.type === 'email' && input.value && input.validity.typeMismatch) message = this.dataset.emailMessage;
        if (error) { error.textContent = message; error.hidden = !message; }
        input.setAttribute('aria-invalid', String(Boolean(message)));
        return !message;
      };
      this.listen(form, 'input', event => { if (inputs.includes(event.target)) validate(event.target); });
      this.listen(form, 'submit', event => {
        const invalid = inputs.filter(input => !validate(input));
        if (invalid.length) { event.preventDefault(); invalid[0].focus(); }
      });
    }
  }
  class ShopGroups extends ScopedElement {
    start() {
      this.listen(document, 'nomosu:shop-filter', event => {
        if (event.detail.group === this.dataset.group) this.apply(event.detail.filter);
      });
      this.listen(window, 'popstate', () => this.apply(new URL(location.href).searchParams.get('filter') || 'all'));
      this.listen(document, 'shopify:section:load', () => this.apply(new URL(location.href).searchParams.get('filter') || 'all'));
      this.listen(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const group = event.target.closest('[data-shop-category]');
        if (group) this.apply(group.dataset.shopCategory);
      });
      this.apply(new URL(location.href).searchParams.get('filter') || 'all');
      document.dispatchEvent(new CustomEvent('nomosu:shop-ready', { detail: { group: this.dataset.group } }));
    }
    categories() { return [...this.querySelectorAll('[data-shop-category]')]; }
    apply(value) {
      const categories = this.categories();
      const filter = categories.some(el => el.dataset.shopCategory === value) ? value : 'all';
      categories.forEach(el => {
        el.hidden = filter === 'all' ? el.dataset.showAll !== 'true' : el.dataset.shopCategory !== filter;
        el.querySelectorAll('[data-exclude-all]').forEach(card => { card.hidden = filter === 'all'; });
        const divider = el.querySelector('[data-category-divider]');
        if (divider) divider.hidden = filter !== 'all';
      });
      document.querySelectorAll('[data-shop-all-content]').forEach(el => {
        if (el.dataset.group === this.dataset.group) el.hidden = filter !== 'all';
      });
      document.dispatchEvent(new CustomEvent('nomosu:shop-selected', { detail: { group: this.dataset.group, filter } }));
    }
  }
  class ShopNav extends ScopedElement {
    start() {
      const update = () => this.render();
      this.listen(document, 'nomosu:shop-ready', update);
      this.listen(document, 'shopify:section:unload', () => queueMicrotask(update));
      this.listen(document, 'nomosu:shop-selected', event => {
        if (event.detail.group !== this.dataset.group) return;
        this.querySelectorAll('a').forEach(a => {
          const active = a.dataset.filter === event.detail.filter;
          a.classList.toggle('active', active);
          if (active) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
        });
      });
      this.listen(this, 'click', event => {
        const link = event.target.closest('a[data-filter]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const url = new URL(location.href);
        if (link.dataset.filter === 'all') url.searchParams.delete('filter'); else url.searchParams.set('filter', link.dataset.filter);
        history.pushState({}, '', url);
        document.dispatchEvent(new CustomEvent('nomosu:shop-filter', { detail: { group: this.dataset.group, filter: link.dataset.filter } }));
      });
      this.render();
    }
    render() {
      const groups = [...document.querySelectorAll('nomosu-shop-groups')].find(el => el.dataset.group === this.dataset.group);
      if (!groups) return;
      const nav = this.querySelector('nav');
      const items = [{ key: 'all', label: this.dataset.allLabel }, ...[...groups.querySelectorAll('[data-shop-category]')].map(el => ({ key: el.dataset.shopCategory, label: el.dataset.navLabel }))];
      const requested = new URL(location.href).searchParams.get('filter') || 'all';
      const active = items.some(item => item.key === requested) ? requested : 'all';
      nav.replaceChildren(...items.map(item => {
        const a = document.createElement('a');
        const url = new URL(location.href);
        if (item.key === 'all') url.searchParams.delete('filter'); else url.searchParams.set('filter', item.key);
        a.href = url.href; a.textContent = item.label; a.dataset.filter = item.key;
        a.classList.toggle('active', item.key === active);
        if (item.key === active) a.setAttribute('aria-current', 'page');
        return a;
      }));
    }
  }
  class ProductVideo extends ScopedElement {
    start() {
      const video = this.querySelector('video');
      const poster = this.querySelector('.product-video-poster');
      if (!video || !poster) return;
      this.listen(poster, 'click', () => {
        poster.hidden = true;
        video.play().then(() => video.focus()).catch(() => { poster.hidden = false; });
      });
      this.listen(video, 'play', () => { poster.hidden = true; });
      this.listen(video, 'ended', () => { poster.hidden = false; });
    }
    disconnectedCallback() { this.querySelector('video')?.pause(); super.disconnectedCallback(); }
  }
  customElements.define('nomosu-product-video', ProductVideo);
  customElements.define('nomosu-product', Product);
  customElements.define('nomosu-accordions', Accordions);
  customElements.define('nomosu-retail-form', RetailForm);
  customElements.define('nomosu-shop-groups', ShopGroups);
  customElements.define('nomosu-shop-nav', ShopNav);
})();
